import React, { useEffect, useState } from 'react';
import { fetchCMSContent } from '../lib/cms';
import { Phone, Info, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const MaterialsGallery = () => {
  const [materials, setMaterials] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(3);

  useEffect(() => {
    const loadMaterials = async () => {
      const data = await fetchCMSContent('materials');
      setMaterials(data);
    };
    loadMaterials();
  }, []);

  const visibleMaterials = materials.slice(0, displayLimit);
  const hasMore = materials.length > displayLimit;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <section id="materialer" className="py-24 bg-zinc-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-black mb-4">Vores materialer</h2>
          <p className="text-xl text-zinc-600 max-w-2xl mx-auto">
            Vi fører kun de bedste materialer til dit projekt. Se et udvalg af vores sortiment herunder.
          </p>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {visibleMaterials.map((item) => (
            <motion.div 
              key={item.id} 
              variants={itemVariants}
              className="bg-white rounded-none overflow-hidden shadow-lg border border-zinc-100 flex flex-col group"
            >
              <div className="h-64 overflow-hidden relative">
                <img
                  src={item.image[0]}
                  alt={item.name.da}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-4 py-2 rounded-none shadow-sm">
                  <p className="text-black font-bold">
                    {item.price_per_unit ? `${item.price_per_unit},- / stk` : `Fra ${item.price_murerbalje},-`}
                  </p>
                </div>
              </div>
              <div className="p-8 flex-1 flex flex-col">
                <h3 className="text-2xl font-black uppercase text-zinc-800 mb-3">{item.name.da}</h3>
                <div 
                  className="text-zinc-600 mb-6 flex-1 prose prose-sm"
                  dangerouslySetInnerHTML={{ __html: item.description.da }}
                />
                <div className="flex items-center justify-between pt-6 border-t border-zinc-100">
                  <Link
                    to={`/materialer/${item.slug.da}`}
                    className="flex items-center gap-1 text-black font-bold hover:text-zinc-700 transition-colors"
                  >
                    Læs mere
                    <ArrowRight size={16} />
                  </Link>
                  <a
                    href="tel:+4520181271"
                    className="flex items-center gap-2 text-zinc-400 font-bold hover:text-zinc-600 transition-colors"
                  >
                    <Phone size={18} />
                    Bestil
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
        {materials.length > 3 && (
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-16 text-center"
          >
            <Link
              to="/materialer"
              className="inline-flex items-center gap-2 bg-black text-white px-8 py-4 rounded-none text-lg font-black uppercase tracking-wider hover:bg-zinc-800 transition-all shadow-xl hover:shadow-black/20 group"
            >
              Se hele udvalget
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default MaterialsGallery;
